// components/jha/SignatureModal.jsx
import React, { useRef, useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    Button,
    Stack,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Draw as DrawIcon,
    AutoAwesome as AutoIcon
} from '@mui/icons-material';
import SignaturePad from 'react-signature-canvas';
import { hasSignatureContent } from '../../utils/utils';

/**
 * Signature pad в модалке.
 *
 * `allowAuto` — показывать ли переключатель Draw/Auto (Auto рисует имя шрифтом
 * вместо живой подписи). В JHA его быть не должно: документ подписывает сам
 * человек, поэтому по умолчанию выключено. Формы вне JHA, которым авто-подпись
 * ещё нужна (CSEP), включают её явно через allowAuto.
 */
export default function SignatureModal({
    open,
    onClose,
    existingSignature = '',
    employeeName = '',
    onSave,
    allowAuto = false
}) {
    const containerRef = useRef(null);
    const sigPadRef = useRef(null);
    const [canvasWidth, setCanvasWidth] = useState(600);
    const [signatureMode, setSignatureMode] = useState('draw');

    useEffect(() => {
        if (!open) return;
        const handleResize = () => {
            if (containerRef.current) {
                const w = containerRef.current.offsetWidth;
                setCanvasWidth(w > 600 ? 600 : w);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [open]);

    useEffect(() => {
        if (!open || !sigPadRef.current) return;
        
        // Reset mode when opening
        setSignatureMode('draw');
        
        if (existingSignature) {
            sigPadRef.current.fromDataURL(existingSignature, {
                ratio: 1,
                width: canvasWidth,
                height: 200
            });
        } else {
            sigPadRef.current.clear();
        }
    }, [open, existingSignature, canvasWidth]);

    if (!open) return null;

    // Generate auto signature
    const generateAutoSignature = () => {
        if (!sigPadRef.current || !employeeName) return;
        
        const canvas = sigPadRef.current.getCanvas();
        const ctx = canvas.getContext('2d');
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Set font style for signature
        ctx.font = 'italic 48px Brush Script MT, cursive';
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        // Draw the name as signature
        ctx.fillText(employeeName, canvas.width / 2, canvas.height / 2);
        
        // Add a simple underline
        ctx.beginPath();
        ctx.moveTo(50, canvas.height / 2 + 30);
        ctx.lineTo(canvas.width - 50, canvas.height / 2 + 30);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();
    };

    // Handle mode change
    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setSignatureMode(newMode);
            
            if (newMode === 'auto') {
                // Generate auto signature when switching to auto mode
                setTimeout(() => {
                    generateAutoSignature();
                }, 100);
            } else {
                // Clear when switching to draw mode
                if (sigPadRef.current) {
                    sigPadRef.current.clear();
                }
            }
        }
    };

    const clearSignature = () => {
        if (sigPadRef.current) {
            sigPadRef.current.clear();
            // If in auto mode, regenerate
            if (signatureMode === 'auto') {
                setTimeout(() => {
                    generateAutoSignature();
                }, 100);
            }
        }
    };

    const acceptSignature = () => {
        if (!sigPadRef.current) return;
        const sig = hasSignatureContent(sigPadRef) ? sigPadRef.current.toDataURL() : '';
        onSave?.(sig);
    };

    return (
        <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        }}>
            <Box
                ref={containerRef}
                sx={{
                    width: '90vw',
                    maxWidth: 650,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    maxHeight: '80vh',
                    overflowY: 'auto'
                }}
            >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Signature for: {employeeName || 'Employee'}
                </Typography>

                {/* Mode Selection — только там, где авто-подпись разрешена */}
                {allowAuto && (
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Signature Type:
                        </Typography>
                        <ToggleButtonGroup
                            value={signatureMode}
                            exclusive
                            onChange={handleModeChange}
                            size="small"
                        >
                            <ToggleButton value="draw">
                                <DrawIcon sx={{ mr: 1 }} />
                                Draw
                            </ToggleButton>
                            <ToggleButton value="auto">
                                <AutoIcon sx={{ mr: 1 }} />
                                Auto
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                )}

                {/* Signature Pad */}
                <Paper 
                    variant="outlined" 
                    sx={{ 
                        p: 1, 
                        mb: 3,
                        bgcolor: signatureMode === 'auto' ? 'grey.50' : 'white'
                    }}
                >
                    <SignaturePad 
                        ref={sigPadRef} 
                        canvasProps={{ 
                            width: canvasWidth, 
                            height: 200, 
                            style: { 
                                display: 'block',
                                border: '1px dashed #ccc',
                                borderRadius: '4px',
                                cursor: signatureMode === 'auto' ? 'not-allowed' : 'crosshair'
                            }
                        }}
                        // Disable drawing in auto mode
                        onBegin={() => {
                            if (signatureMode === 'auto') {
                                return false;
                            }
                        }}
                    />
                </Paper>

                {/* Action Buttons */}
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <Button 
                            variant="outlined" 
                            fullWidth
                            onClick={clearSignature}
                            disabled={signatureMode === 'auto'}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button 
                            variant="contained" 
                            color="primary"
                            fullWidth
                            onClick={acceptSignature}
                        >
                            Accept
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button 
                            variant="outlined" 
                            color="inherit" 
                            fullWidth
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}